import {
  AlertCircleIcon,
  ChevronDownIcon,
  ClockIcon,
  Percent03Icon,
  Sliders04Icon,
} from '@langchain/untitled-ui-icons';

import React, { useMemo } from 'react';

import {
  AlertAggregation,
  AlertAttribute,
  AlertOperator,
} from '@/types/alerts';
import { FeedbackStatsField, RunStatsSchema } from '@/types/schema';
import { cn } from '@/utils/tailwind';

import { FeedbackChip } from '../FeedbackChips';
import { FilterBar } from '../FilterBar/FilterBar';
import { FilterLabel } from '../FilterBar/constants';
import { PopoverDropdownMenu } from '../PopoverDropdownMenu';
import { AlertButtonGroup, AlertButtonGroupOption } from './AlertButtons';
import { useAlertState } from './useAlertState';
import { getChartFilter, getPlaceholder } from './utils';

const ERRORED_RUN_OPTIONS: AlertButtonGroupOption<AlertAggregation>[] = [
  { value: 'sum', label: 'Count' },
  { value: 'pct', label: 'Percent' },
];

const FEEDBACK_SCORE_OPERATOR_OPTIONS: AlertButtonGroupOption<AlertOperator>[] =
  [
    { value: 'gte', label: '>=' },
    { value: 'lte', label: '<=' },
  ];

const ALERT_TIME_OPTIONS: AlertButtonGroupOption<'5' | '15'>[] = [
  { value: '5' as const, label: '5' },
  { value: '15' as const, label: '15' },
];

const ALERT_ATTRIBUTE_OPTIONS: AlertButtonGroupOption<AlertAttribute>[] = [
  {
    value: AlertAttribute.run_count,
    label: 'Errored Runs',
    icon: <AlertCircleIcon className="h-5 w-5" />,
  },
  {
    value: AlertAttribute.feedback_score,
    label: 'Feedback Score',
    icon: <Sliders04Icon className="h-5 w-5" />,
  },
  {
    value: AlertAttribute.run_latency,
    label: 'Latency',
    icon: <ClockIcon className="h-5 w-5" />,
  },
];

function ThresholdInput() {
  const { threshold, isThresholdValid, setThreshold, attribute, aggregation } =
    useAlertState();

  return (
    <div className="relative">
      <input
        value={threshold}
        onChange={(e) => setThreshold(e.target.value)}
        placeholder={getPlaceholder(attribute, aggregation)}
        className={cn(
          'w-10 rounded-md border bg-transparent py-1 text-center text-sm',
          'placeholder:text-tertiary',
          isThresholdValid
            ? 'border-secondary focus:border-brand'
            : 'border-error focus:border-error'
        )}
      />
    </div>
  );
}

function Window() {
  const { windowMinutes, setWindowMinutes } = useAlertState();
  return (
    <>
      <AlertButtonGroup
        options={ALERT_TIME_OPTIONS}
        value={windowMinutes}
        onChange={setWindowMinutes}
      />
      <span className="text-sm">minutes.</span>
    </>
  );
}

function ErroredRunsCondition() {
  const { aggregation, setAggregation } = useAlertState();

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className=" text-sm">Errored runs</span>
      <AlertButtonGroup<AlertAggregation>
        options={ERRORED_RUN_OPTIONS}
        value={aggregation}
        onChange={setAggregation}
      />
      <span className=" text-sm">exceeds</span>
      <ThresholdInput />
      <span className=" text-sm">
        {aggregation === 'sum' ? 'runs in the last' : '% in the last'}
      </span>
      <Window />
    </div>
  );
}

function FeedbackScoreCondition({
  feedbackStats,
}: {
  feedbackStats?: FeedbackStatsField;
}) {
  const { feedbackKey, operator, setFeedbackKey, setOperator } =
    useAlertState();

  const feedbackKeys = Object.keys(feedbackStats ?? {});
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm">The average</span>
      <PopoverDropdownMenu
        values={feedbackKeys}
        getTitle={(value) => value}
        onSelect={(value) => {
          setFeedbackKey(value);
        }}
        getRowTitleComponent={(value) => {
          return <FeedbackChip feedback_key={value} />;
        }}
        getIsSelected={(value) => value === feedbackKey}
        enableSearch
      >
        <div
          className={
            'flex items-center gap-2  rounded-md border border-secondary px-3 py-1 text-sm transition-colors'
          }
          role="button"
        >
          {feedbackKey ? (
            <FeedbackChip
              className="border-none py-0 text-sm"
              feedback_key={feedbackKey}
            />
          ) : (
            <span className="text-sm font-light">feedback metric</span>
          )}
          <ChevronDownIcon className="h-4 w-4" />
        </div>
      </PopoverDropdownMenu>
      <span className=" text-sm">is</span>
      <AlertButtonGroup<AlertOperator>
        options={FEEDBACK_SCORE_OPERATOR_OPTIONS}
        value={operator}
        onChange={(comparison) => setOperator(comparison)}
      />
      <ThresholdInput />
      <span className=" text-sm">in the last</span>
      <Window />
    </div>
  );
}

function LatencyCondition() {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm">The average trace latency exceeds</span>
      <ThresholdInput />
      <span className="text-sm">seconds in the last</span>
      <Window />
    </div>
  );
}

export function AlertOptions({ runStats }: { runStats?: RunStatsSchema }) {
  const {
    attribute,
    setAttribute,
    setAggregation,
    isThresholdValid,
    setFilter,
    filter,
    name,
    setName,
    aggregation,
  } = useAlertState();

  const disabledRunFields = useMemo(() => {
    if (
      attribute === AlertAttribute.error_count ||
      attribute === AlertAttribute.run_count
    ) {
      return [FilterLabel.status];
    }
    return [];
  }, [attribute]);

  const onChangeMetric = (metric: AlertAttribute) => {
    setAttribute(metric);
    setFilter({ filter: getChartFilter(metric) });

    // Set default aggregation based on the metric
    if (
      metric === AlertAttribute.error_count ||
      metric === AlertAttribute.run_count
    ) {
      setAggregation('sum');
    } else {
      setAggregation('avg');
    }
  };

  const attributeToComponent: Record<AlertAttribute, React.ReactNode> = {
    [AlertAttribute.error_count]: <ErroredRunsCondition />,
    [AlertAttribute.run_count]: <ErroredRunsCondition />,
    [AlertAttribute.feedback_score]: (
      <FeedbackScoreCondition feedbackStats={runStats?.feedback_stats} />
    ),
    [AlertAttribute.latency]: <LatencyCondition />,
    [AlertAttribute.run_latency]: <LatencyCondition />,
  };

  return (
    <>
      <div className="flex flex-col gap-1">
        <h3 className="text-md font-medium">Alert Name</h3>
        <div>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-md border border-secondary bg-transparent px-3 py-1.5 text-sm focus:border-brand"
          />
        </div>
      </div>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <h3 className="text-md font-medium">Alert Metric</h3>
          <p className="text-sm text-tertiary">Select what you want to track</p>
        </div>
        <AlertButtonGroup<AlertAttribute>
          options={ALERT_ATTRIBUTE_OPTIONS}
          value={attribute}
          onChange={onChangeMetric}
          stack
        />
        {attribute !== AlertAttribute.feedback_score && (
          <div className="flex flex-col gap-2">
            <span className="text-sm">Filtered On:</span>
            <div>
              <FilterBar
                stats={runStats}
                variant="run-details"
                value={filter}
                onChange={setFilter}
                disabledAlertRunFields={disabledRunFields}
                alertFilteringEnabled
                hideAIQuery
              />
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-col gap-4">
        <h3 className="text-md font-medium">Alert When:</h3>
        {attributeToComponent[attribute]}
        {!isThresholdValid && (
          <div className="text-sm text-error">
            Please use positive digits only
          </div>
        )}
        {(attribute === AlertAttribute.run_count ||
          attribute === AlertAttribute.error_count) &&
          aggregation === 'pct' && (
            <div className="flex items-center gap-4 rounded-lg border border-secondary p-4 shadow-lg">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md border border-secondary">
                <Percent03Icon className="h-4 w-4" />
              </div>
              <p className="text-sm font-medium text-primary">
                Error percentage = (Errored runs matching your filter / Total
                runs matching your filter) x 100
              </p>
            </div>
          )}
      </div>
    </>
  );
}
